import {
  UserAnalysisActionsEnum,
  userAnalysisStartSession,
  userAnalysisTrackAction,
  UserAnalysisTrackActionType
} from '@/api/userAnalysis'
import { KeepAlive, ref } from 'vue'
import { ActionFactory } from './actionFactory'
import { RouteLocationNormalized } from 'vue-router'

class UserAnalysis {
  private _isInitialized = ref(false)
  private _actions = ref<UserAnalysisTrackActionType[]>([])

  private _actionFactory: ActionFactory

  constructor() {
    this._actionFactory = new ActionFactory()
  }

  // 停留时间统计
  private _visibilityStartTime = ref(0)

  get isInitialized() {
    return this._isInitialized.value
  }

  private get sessionId() {
    return this._actionFactory.sessionId
  }

  private set sessionId(value: string) {
    this._actionFactory.sessionId = value
  }

  public startSession = async () => {
    if (this._isInitialized.value) return

    this._isInitialized.value = true
    this.sessionId = await userAnalysisStartSession({
      page_url: window.location.href
    })
  }

  public endSession = () => {
    console.log('endSession')
  }

  private trackAction = (action: UserAnalysisTrackActionType) => {
    this._actions.value.push(action)
  }

  private trackActions = (actions: UserAnalysisTrackActionType[]) => {
    this._actions.value.push(...actions)
  }

  public trackUserAction = ({
    actionType,
    actionDetails
  }: {
    actionType: UserAnalysisActionsEnum
    actionDetails: any
  }) => {
    this.trackAction(this._actionFactory.userAction({ actionType, actionDetails }))
  }

  // 批量跟踪用户行为
  public trackUserActions = (
    actions: {
      actionType: UserAnalysisActionsEnum
      actionDetails: any
    }[],
    { immediately = false }: { immediately?: boolean } = {}
  ) => {
    this.trackActions(actions.map(action => this._actionFactory.userAction(action)))
    if (immediately) {
      this.sendActions()
    }
  }

  public trackVisitAction = () => {
    this._visibilityStartTime.value = Date.now()
  }

  public trackLeaveAction = () => {
    if (this._visibilityStartTime.value === 0) {
      return
    }

    const _leaveTime = Date.now()
    const _duration = _leaveTime - this._visibilityStartTime.value
    if (_duration <= 3000) {
      return
    }

    this.trackAction(
      this._actionFactory.visitAction({
        details: {
          time: this._visibilityStartTime.value,
          leaveTime: _leaveTime,
          duration: _duration
        }
      })
    )
    this._visibilityStartTime.value = 0
  }

  public trackPageNavigation = (from: RouteLocationNormalized) => {
    const _leaveTime = Date.now()
    const _duration = _leaveTime - this._visibilityStartTime.value
    if (_duration <= 3000) {
      return
    }

    this.trackAction(
      this._actionFactory.visitAction({
        details: {
          time: this._visibilityStartTime.value,
          leaveTime: _leaveTime,
          duration: _duration
        },
        navigateFrom: from
      })
    )

    this._visibilityStartTime.value = _leaveTime
  }

  public sendActions = (options: { keepalive?: boolean } = {}) => {
    if (this._actions.value.length === 0) {
      return
    }

    try {
      userAnalysisTrackAction(this._actions.value, options)
    } catch (error) {
      console.error(error)
    } finally {
      this._actions.value = []
    }
  }
}

const userAnalysis = new UserAnalysis()

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    userAnalysis.trackLeaveAction()

    // 发送请求
    userAnalysis.sendActions()
  } else {
    userAnalysis.trackVisitAction()
  }
})

document.addEventListener('beforeunload', () => {
  userAnalysis.trackLeaveAction()

  // 发送请求
  userAnalysis.sendActions({ keepalive: true })
})

export const useUserAnalysis = () => {
  return userAnalysis
}

// plugins/i18n.js
export default {
  install: (app: any, options: any) => {
    app.config.globalProperties.$userAnalysis = userAnalysis
  }
}
